import {
  pickDistinctIndices,
  shuffleAvoidingFixedPositions,
} from '../../games/engine/boardMath';

import type { GridConfig } from './types';

/**
 * A layer's problem pool. `topEQs[i]` is the equation and `bottomAnswers[i]`
 * its answer - the same parallel-array shape the CreateJS games use.
 */
export interface ProblemPool {
  topEQs: readonly string[];
  bottomAnswers: readonly string[];
}

export interface Deal {
  equations: readonly string[];
  expected: readonly string[];
  placed: readonly string[];
  /** Pool indices used by this deal, for the caller's `blnChosen` bookkeeping. */
  chosenIndices: readonly number[];
}

/**
 * Draws one board from `pool`. Ported from the fill loop in
 * `games/engine/rulesBoardEngine.ts:319-362` - same helpers, same order, so
 * the DOM board and the canvas board deal identically given the same RNG.
 */
export function dealBoard(
  pool: ProblemPool,
  config: GridConfig,
  rng: () => number,
): Deal {
  const chosenIndices = pickDistinctIndices(
    pool.bottomAnswers.length,
    config.rows * config.cols,
    rng,
  );

  const equations = chosenIndices.map((index) => pool.topEQs[index]!);
  const expected = chosenIndices.map((index) => pool.bottomAnswers[index]!);

  // Derangement: no answer starts in the cell it belongs to.
  const placed = shuffleAvoidingFixedPositions(expected, rng);

  return { equations, expected, placed, chosenIndices };
}
